import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Box, Typography, Alert, Dialog, DialogTitle, DialogContent, DialogActions,
  Button, Chip, Stack, Grid, Card, CardContent,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { useSnackbar } from 'notistack';
import { useNavigate, useParams, useLocation, useSearchParams } from 'react-router-dom';
import { transformarCotizacionAPedido } from '../../utils/cotizacionToPedido';
import cotizacionVentaService from '../../services/cotizacionVentaService';
import pedidoVentaService, { PedidoVenta, PedidoVentaDetalle } from '../../services/pedidoVentaService';
import api from '../../services/api';
import clienteService, { OPTIONS_CLIENTE_OPERATIVO } from '../../services/clienteService';
import ubicacionService from '../../services/ubicacionService';
import {
  VentaPendienteMaster,
  cantidadSugeridaDesdePendienteVenta,
} from '../../services/ventasSeguimientoService';
import ClienteInfo360Dialog from '../clientes/info360/ClienteInfo360Dialog';
import FacturaPOSClientePanel from '../facturaventa/pos/FacturaPOSClientePanel';
import FacturaPOSSearchBar, { SuggestionItem } from '../facturaventa/pos/FacturaPOSSearchBar';
import FacturaPOSLineasGrid from '../facturaventa/pos/FacturaPOSLineasGrid';
import FacturaPOSTotalesPanel, { POSCompaniaOption } from '../facturaventa/pos/FacturaPOSTotalesPanel';
import { POSCliente, POSLinea } from '../facturaventa/pos/types';
import { construirLineasDesdeUltimoPedido } from '../facturaventa/pos/repetirUltimoPedido';
import productoService, { OPTIONS_PRODUCTO_OPERATIVO } from '../../services/productoService';
import companiaService from '../../services/companiaService';
import { calcularTotalesIvaVenta } from '../../utils/calcularTotalesIvaVenta';
import { obtenerCodigoListaCliente, obtenerPrecioBaseProducto, resolverPrecioDesdeLista } from '../../utils/precioListaPedido';
import { precioListaDesdePendienteMaster } from '../../utils/pedidoVentaLineas';
import { resolverUmYPrecioDesdeDetalle } from '../../utils/umVentaDocumento';
import { fetchStockDisponibleEnUm } from '../../utils/stockUmVenta';
import articuloXListaPrecioService from '../../services/articuloXListaPrecioService';
import {
  contextoDescuentoDesdeCliente,
  resolverBonificacionesCliente,
  resolverPorcDescLinea,
} from '../../services/descuentoService';
import { formatNroDocumentoVenta, parseLocalDate, toLocalDateStringYMD, truncToDecimals } from '../../utils/formatters';
import { getApiErrorMessage } from '../../utils/apiError';
import { useSucursalesUsuarioContext } from '../../contexts/SucursalesUsuarioContext';
import { persistGlobalNroSuc } from '../../hooks/useSucursalesVentaFiltro';
import { resolveSucursalPreferidaNro, filtrarSucursalesPorCompania, resolveNroSucParaCompania, resolveCompaniaYSucursalInicial, persistCompaniaYSucursalInicial } from '../../utils/sucursalPreferida';
import { persistGlobalIdCompania } from '../../utils/companiaPreferida';
import {
  calcularFechaVencimDesdeVtoComprob,
  sameCalendarDay,
  toDateOnly,
  toLocalDateInputValue,
  validarFechasFacturaVenta,
} from '../../utils/facturaVentaFechas';
import { mapearVencimComprobanteCargado } from '../../utils/facturaVentaVencim';
import { resolveVtoComprobCliente } from '../../utils/vtoComprobCliente';
import {
  aplicarPorcentajeDescuento,
  esTipoIvaPreciosConIva,
  multiplicadorIvaProducto,
  round2,
  subtotalLineaConIvaDisplay,
} from '../facturaventa/pos/preciosIvaDisplay';
import { advertirLineasConPrecioCeroPedido } from '../../utils/pedidoVentaLineas';

type PedidoVentaUI = PedidoVenta & { ordenCompra?: string; transporte?: string; ubicacion?: string; formaDePago?: string };
type PedidoVentaUIExt = PedidoVentaUI & {
  nroSuc?: number;
  letra?: string;
  nroComprobante?: number;
  cuit?: string;
  tipoIVA?: string;
  idCompania?: string;
  vtoComprob?: number | null;
};

interface SucursalVenta {
  nroSuc: number;
  nombreSucursal?: string;
  idCompania?: string | null;
}

const FORMAS_DE_PAGO_VALIDAS = ['CONTADO', 'CTA.CTE.', 'EFECTIVO', 'DEVOLUCION', 'CONTRAREEMBOLSO'];
const DEFAULT_NRO_SUC = 1;
/** Pedido nuevo: 0 para que FormularioVentaForm asigne la primera sucursal de la lista cargada. */
const NRO_SUC_PEDIDO_NUEVO = 0;

const genId = () => `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;

const calcularSubtotal = (l: Pick<POSLinea, 'cantidad' | 'precioUnitario' | 'descuento'>): number => {
  const base = round2((Number(l.cantidad) || 0) * (Number(l.precioUnitario) || 0));
  return aplicarPorcentajeDescuento(base, l.descuento);
};

const obtenerTransporteSugerido = (cliente?: { nroExpreso?: number | string | null } | null): string => {
  if (!cliente) return '';
  const rawValue = cliente.nroExpreso ?? (cliente as any)?.nroexpreso ?? null;
  if (rawValue === undefined || rawValue === null) return '';
  const strValue = String(rawValue).trim();
  return !strValue || strValue === '0' ? '' : strValue;
};

const normalizarFormaDePago = (valor?: string | null): string => {
  if (!valor) return '';
  const cleaned = String(valor).trim().toUpperCase();
  return FORMAS_DE_PAGO_VALIDAS.find(f => f.toUpperCase() === cleaned) || '';
};

const obtenerFormaPagoSugerida = (cliente?: { condVta?: string | null } | null): string => {
  if (!cliente) return 'EFECTIVO';
  return normalizarFormaDePago(cliente.condVta ?? (cliente as any)?.condvta ?? null);
};

const lineasDesdeDetalles = (detalles: PedidoVentaDetalle[]): POSLinea[] =>
  detalles.map((d) => {
    const cantidad = Number(d.cantidad ?? 0);
    const precioUnitario = Number(d.precioUnitario ?? 0);
    const descuento = Number(d.descuento ?? d.porcDesc ?? 0);
    const precioUnitarioFinal = Number(d.precioUnitarioFinal ?? 0);
    const subtotalFinal = Number(d.subtotalFinal ?? 0);
    const linea: POSLinea = {
      id: genId(),
      codigoArticulo: String(d.codigoArticulo ?? '').trim(),
      descripcionArticulo: d.descripcionArticulo || '',
      cantidad,
      precioUnitario,
      descuento,
      descuentoManual: true,
      subtotal: Number(d.subtotal ?? calcularSubtotal({ cantidad, precioUnitario, descuento })),
      um: (d as any).um || (d as any).Um || (d as any).UM || '',
      notas: (d as any).notas,
      stockDisponible: (d as any).stockDisponible,
      nroFormularioMaster: (d as any).nroFormularioMaster,
      nroItemMaster: (d as any).nroItemMaster,
      ...(precioUnitarioFinal > 0 ? { precioUnitarioFinal } : {}),
      ...(subtotalFinal > 0 ? { subtotalFinal } : {}),
    };
    return linea;
  });

const clienteDesdeData = (data: any): POSCliente => ({
  nroCliente: data.nroCliente,
  nombre: data.nombre,
  cuit: data.cuit || '',
  tipoIva: data.tipoIVA || data.tipoIva || '',
  nroLista: data.nroLista || '',
  condVta: data.formaDePago || data.condVta || '',
});

const pedidoVentaInicial: PedidoVentaUIExt = {
  nroFormulario: 0,
  nroTipoFormulario: '12',
  tipoCliente: 'C',
  fechaEmision: new Date(),
  fechaVencim: new Date(),
  horaEmision: new Date(),
  nroCliente: 0,
  nombre: '',
  nroVendedor: 0,
  nroLista: '',
  codigoMoneda: 'ARS',
  importeTotal: 0,
  asentado: 'N',
  descripcion: '',
  usuario: '',
  ordenCompra: '',
  transporte: '',
  ubicacion: '',
  formaDePago: '',
  nroSuc: NRO_SUC_PEDIDO_NUEVO,
  cuit: '',
  tipoIVA: '',
  idCompania: '',
  detalles: [],
  porcDesc: 0,
  porcDesc2: 0,
  porcDesc3: 0,
};

const PedidosVenta: React.FC = () => {
  const navigate = useNavigate();
  const { pedidoId } = useParams<{ pedidoId?: string }>();
  const [searchParams] = useSearchParams();
  const cotizacionIdParam = searchParams.get('cotizacionId');
  const cotizacionId = cotizacionIdParam ? parseInt(cotizacionIdParam, 10) : null;
  const location = useLocation();
  const {
    sucursales,
    preferidaNro,
    preferidaSucursal,
    loading: loadingSucursalesUsuario,
  } = useSucursalesUsuarioContext();
  const sucursalUsuarioEditadaRef = useRef(false);
  const initCompaniaSucursalRef = useRef(false);

  const modo = pedidoId
    ? (location.pathname.includes('/ver') ? 'ver' : 'editar')
    : 'crear';
  const isReadOnly = modo === 'ver';
  const isEditMode = modo === 'editar';
  /** Hay ID en la URL (editar/ver): no sobrescribir cabecera con defaults del cliente. */
  const esPedidoPersistente = Boolean(pedidoId);

  /** Pedido nuevo cargado desde ?cotizacionId= (botón Preparar cotización) */
  const preparandoDesdeCotizacion = Boolean(cotizacionId && !isNaN(cotizacionId)) && !pedidoId;

  const [loading, setLoading] = useState(false);
  const [loadingPedido, setLoadingPedido] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [pedidoVenta, setPedidoVenta] = useState<PedidoVentaUIExt>(pedidoVentaInicial);
  const [posCliente, setPosCliente] = useState<POSCliente | null>(null);
  const [lineasPOS, setLineasPOS] = useState<POSLinea[]>([]);
  const [productosIvaCache, setProductosIvaCache] = useState<Record<string, unknown>>({});
  const [pedidoPersistido, setPedidoPersistido] = useState<{
    nroFormulario: number;
    importeTotal: number;
    importeSinIva: number;
    ivainscripto: number;
    tipoIva?: string;
  } | null>(null);
  const [companias, setCompanias] = useState<POSCompaniaOption[]>([]);
  const loadingSucursales = loadingSucursalesUsuario;
  const [infoCotizacion, setInfoCotizacion] = useState<{ letra?: string; puntoVenta?: number; nroComprobante?: number; nroFormulario?: number } | null>(null);
  const [successDialogOpen, setSuccessDialogOpen] = useState(false);
  const [nroPedidoCreado, setNroPedidoCreado] = useState<number | null>(null);
  const [pendingApplied, setPendingApplied] = useState(false);
  const [loadingRepetirUltimoPedido, setLoadingRepetirUltimoPedido] = useState(false);
  const [info360Cliente, setInfo360Cliente] = useState<number | null>(null);
  const { enqueueSnackbar } = useSnackbar();
  const umBackfillTriedRef = useRef<Set<string>>(new Set());

  const puedeRepetirUltimoPedido = !isReadOnly && !isEditMode && !preparandoDesdeCotizacion;

  const ventaPendiente = (location.state as { ventaPendiente?: VentaPendienteMaster } | null)?.ventaPendiente;

  // Resetear infoCotizacion cuando no hay cotizacionId
  useEffect(() => {
    if (!cotizacionId || isNaN(cotizacionId)) setInfoCotizacion(null);
  }, [cotizacionId]);

  // Venta pendiente → agregar línea POS
  useEffect(() => {
    if (pendingApplied || !ventaPendiente || pedidoId || (cotizacionId && !isNaN(cotizacionId))) return;

    setLineasPOS((prev) => {
      const alreadyIncluded = prev.some(
        (l: any) =>
          l.nroFormularioMaster === ventaPendiente.nroFormularioMaster &&
          l.nroItemMaster === ventaPendiente.nroItemMaster,
      );
      if (alreadyIncluded) return prev;

      const cantidad = cantidadSugeridaDesdePendienteVenta(ventaPendiente);
      const precioLista = precioListaDesdePendienteMaster(ventaPendiente);
      const porcDescDet = Number(ventaPendiente.porcDesc1Master ?? 0) || 0;
      const subtotal = round2(cantidad * (precioLista - (precioLista * porcDescDet) / 100));

      return [
        ...prev,
        {
          id: genId(),
          codigoArticulo: ventaPendiente.codigoArticulo || '',
          descripcionArticulo: ventaPendiente.descripcionArticulo || '',
          cantidad,
          precioUnitario: precioLista,
          descuento: porcDescDet,
          descuentoManual: true,
          subtotal,
          um: '',
          nroFormularioMaster: ventaPendiente.nroFormularioMaster,
          nroItemMaster: ventaPendiente.nroItemMaster,
        } as any,
      ];
    });

    if (ventaPendiente.nroCliente && ventaPendiente.nroCliente > 0) {
      setPedidoVenta((prev) => ({
        ...prev,
        nroCliente: ventaPendiente.nroCliente!,
        nombre: ventaPendiente.nombreCliente || prev.nombre,
      }));
    }

    setPendingApplied(true);
  }, [pendingApplied, ventaPendiente, pedidoId, cotizacionId]);

  // Completar U/M vacía y stock en esa UM desde Logística + equivalencias
  useEffect(() => {
    const pendientes = lineasPOS.filter((l) => {
      const cod = String(l.codigoArticulo ?? '').trim();
      return Boolean(cod) && !String(l.um ?? '').trim() && !umBackfillTriedRef.current.has(l.id);
    });
    if (pendientes.length === 0) return;

    pendientes.forEach((l) => umBackfillTriedRef.current.add(l.id));
    let cancelled = false;

    void (async () => {
      const updates = new Map<
        string,
        { um: string; precioUnitario: number; stockDisponible: number | null; usoEquivalencia: boolean }
      >();
      await Promise.all(
        pendientes.map(async (l) => {
          const cod = String(l.codigoArticulo ?? '').trim();
          try {
            const detalle = await productoService.getEquivalenciasUmDetalle(cod);
            const r = resolverUmYPrecioDesdeDetalle(
              { codigoArticulo: cod },
              Number(l.precioUnitario) || 0,
              'pedido',
              detalle,
            );
            if (!r.um) return;
            const stockDisponible = await fetchStockDisponibleEnUm(cod, r.um, detalle);
            updates.set(l.id, {
              um: r.um,
              precioUnitario: r.precioUnitario,
              stockDisponible,
              usoEquivalencia: r.usoEquivalencia,
            });
          } catch {
            /* sin UM */
          }
        }),
      );
      if (cancelled || updates.size === 0) return;
      setLineasPOS((prev) =>
        prev.map((l) => {
          const u = updates.get(l.id);
          if (!u || String(l.um ?? '').trim()) return l;
          const precioUnitario = u.usoEquivalencia ? u.precioUnitario : Number(l.precioUnitario) || 0;
          const next = {
            ...l,
            um: u.um,
            precioUnitario,
            stockDisponible: u.stockDisponible ?? l.stockDisponible,
          };
          return { ...next, subtotal: calcularSubtotal(next) };
        }),
      );
    })();

    return () => {
      cancelled = true;
    };
  }, [lineasPOS]);

  // Cargar cotización para pre-cargar pedido (modo crear con ?cotizacionId=)
  useEffect(() => {
    if (cotizacionId && !isNaN(cotizacionId) && !pedidoId) {
      const cargarDesdeCotizacion = async () => {
        try {
          setLoadingPedido(true);
          setError(null);
          const cotizacion = await cotizacionVentaService.getCotizacionVenta(cotizacionId);
          const cotizacionExt = cotizacion as any;

          setInfoCotizacion({
            letra: cotizacionExt.letra || '',
            puntoVenta: cotizacionExt.nroSuc ?? 0,
            nroComprobante: cotizacionExt.nroComprobante ?? 0,
            nroFormulario: cotizacion.nroFormulario,
          });

          let ubicacionFormateada = cotizacionExt.ubicacion || '';
          const nroUbicacionOrigen = cotizacionExt.nroUbicacionOrigen || cotizacionExt.nroUbicacion;
          if (!ubicacionFormateada && nroUbicacionOrigen) {
            try {
              const ubicaciones = await ubicacionService.getUbicaciones();
              const encontrada = ubicaciones.find(u => u.nroUbicacion === nroUbicacionOrigen);
              ubicacionFormateada = encontrada
                ? `${encontrada.nombreUbicacion} (${encontrada.nroUbicacion})`
                : `(${nroUbicacionOrigen})`;
            } catch {
              ubicacionFormateada = nroUbicacionOrigen?.toString() || '';
            }
          }

          let transporteValue = cotizacionExt.transporte || '';
          const nroExpreso = cotizacionExt.nroExpreso;
          if (!transporteValue && nroExpreso !== undefined && nroExpreso !== null && nroExpreso !== 0 && nroExpreso !== '') {
            transporteValue = String(nroExpreso);
          } else if (transporteValue) {
            const match = String(transporteValue).match(/\((\d+)\)/);
            transporteValue = match && match[1] ? match[1] : String(transporteValue);
          }

          const pedidoTransformado = transformarCotizacionAPedido(cotizacion);
          const detallesConSubtotales = pedidoTransformado.detalles?.map((d) => ({
            ...d,
            precioUnitario: (d as any).precioLista || d.precioUnitario,
            descuento: (d as any).porcDesc ?? d.descuento,
          })) || [];

          const importeTotalCalculado = detallesConSubtotales.reduce((s, d) => s + (d.subtotal || 0), 0);
          const fechaEmisionDate = pedidoTransformado.fechaEmision
            ? (pedidoTransformado.fechaEmision instanceof Date ? pedidoTransformado.fechaEmision : new Date(pedidoTransformado.fechaEmision))
            : new Date();
          const vencimComprobante = mapearVencimComprobanteCargado(cotizacion, fechaEmisionDate);
          const horaEmisionDate = pedidoTransformado.horaEmision
            ? (pedidoTransformado.horaEmision instanceof Date ? pedidoTransformado.horaEmision : new Date(pedidoTransformado.horaEmision))
            : new Date();

          const pedidoCargado: PedidoVentaUIExt = {
            ...pedidoTransformado,
            fechaEmision: fechaEmisionDate,
            fechaVencim: vencimComprobante.fechaVencim,
            vtoComprob: vencimComprobante.vtoComprob,
            horaEmision: horaEmisionDate,
            ordenCompra: cotizacionExt.ordenDeCompra || cotizacionExt.ordenCompra || '',
            transporte: transporteValue,
            ubicacion: ubicacionFormateada,
            formaDePago: cotizacionExt.formaDePago || '',
            nroSuc:
              cotizacionExt.nroSuc != null && Number(cotizacionExt.nroSuc) > 0
                ? Number(cotizacionExt.nroSuc)
                : NRO_SUC_PEDIDO_NUEVO,
            cuit: cotizacionExt.cuit || '',
            tipoIVA: cotizacionExt.tipoIVA || cotizacionExt.tipoIva || '',
            idCompania: cotizacionExt.idCompania || '',
            detalles: detallesConSubtotales,
            importeTotal: pedidoTransformado.importeTotal && pedidoTransformado.importeTotal > 0
              ? pedidoTransformado.importeTotal
              : round2(importeTotalCalculado),
          } as PedidoVentaUIExt;

          setPedidoVenta(pedidoCargado);
          setLineasPOS(lineasDesdeDetalles(detallesConSubtotales));
          setPosCliente(clienteDesdeData(pedidoCargado));
        } catch (err: unknown) {
          const message = getApiErrorMessage(err, 'Error al cargar la cotización');
          setError(message);
          enqueueSnackbar(message, { variant: 'error', autoHideDuration: 5000 });
        } finally {
          setLoadingPedido(false);
        }
      };
      cargarDesdeCotizacion();
      return;
    }

    if (pedidoId) {
      const cargarPedido = async () => {
        try {
          setLoadingPedido(true);
          setError(null);
          const id = parseInt(pedidoId, 10);
          if (isNaN(id)) {
            setError('ID de pedido inválido');
            navigate('/ventas/pedidos');
            return;
          }

          const pedidoCargado = await pedidoVentaService.getPedidoVenta(id);

          let ubicacionFormateada = (pedidoCargado as any).ubicacion || '';
          const nroUbicacionOrigen =
            (pedidoCargado as any).nroUbicacionOrigen ||
            (pedidoCargado as any).nroUbicacion ||
            (pedidoCargado as any).formulario?.nroUbicacionOrigen;

          if (!ubicacionFormateada && nroUbicacionOrigen) {
            try {
              const ubicaciones = await ubicacionService.getUbicaciones();
              const encontrada = ubicaciones.find(u => u.nroUbicacion === nroUbicacionOrigen);
              ubicacionFormateada = encontrada
                ? `${encontrada.nombreUbicacion} (${encontrada.nroUbicacion})`
                : `(${nroUbicacionOrigen})`;
            } catch {
              ubicacionFormateada = nroUbicacionOrigen?.toString() || '';
            }
          }

          let transporteValue = (pedidoCargado as any).transporte || '';
          const nroExpreso = (pedidoCargado as any).nroExpreso;
          if (!transporteValue && nroExpreso !== undefined && nroExpreso !== null && nroExpreso !== 0 && nroExpreso !== '') {
            transporteValue = String(nroExpreso);
          } else if (transporteValue) {
            const match = String(transporteValue).match(/\((\d+)\)/);
            transporteValue = match && match[1] ? match[1] : String(transporteValue);
          }

          const detallesConSubtotales = pedidoCargado.detalles?.map((detalle) => {
            const precioUnitario = Number(detalle.precioUnitario ?? detalle.precioLista ?? 0);
            const porcDesc = Number(detalle.porcDesc ?? detalle.descuento ?? 0);
            return {
              ...detalle,
              precioUnitario,
              descuento: porcDesc,
              porcDesc,
              subtotal: detalle.subtotal ?? 0,
            };
          }) || [];

          let tipoIvaResuelto = String(
            (pedidoCargado as any).tipoIVA ?? (pedidoCargado as any).tipoIva ?? '',
          ).trim();
          if (!tipoIvaResuelto && pedidoCargado.nroCliente) {
            try {
              const c = await clienteService.getCliente(Number(pedidoCargado.nroCliente), OPTIONS_CLIENTE_OPERATIVO);
              tipoIvaResuelto = String(c.tipoIva ?? '').trim();
            } catch {
              /* sin cliente */
            }
          }

          const codigos: string[] = Array.from(
            new Set(
              detallesConSubtotales
                .map((d) => String(d.codigoArticulo ?? '').trim())
                .filter((c) => c.length > 0),
            ),
          );
          const fetchedProducts: Record<string, unknown> = {};
          await Promise.all(
            codigos.map(async (codigo) => {
              try {
                const producto = await productoService.getProducto(codigo, OPTIONS_PRODUCTO_OPERATIVO);
                if (producto) fetchedProducts[codigo] = producto;
              } catch {
                /* sin producto */
              }
            }),
          );
          if (Object.keys(fetchedProducts).length > 0) {
            setProductosIvaCache((prev) => ({ ...prev, ...fetchedProducts }));
          }

          setPedidoPersistido({
            nroFormulario: Number(pedidoCargado.nroFormulario ?? id),
            importeTotal: Number(pedidoCargado.importeTotal ?? 0),
            importeSinIva: Number((pedidoCargado as any).importeSinIva ?? 0),
            ivainscripto: Number((pedidoCargado as any).ivainscripto ?? 0),
            tipoIva: tipoIvaResuelto || undefined,
          });

          const importeTotalCalculado = detallesConSubtotales.reduce((s, d) => s + (d.subtotal || 0), 0);
          const fechaEmisionDate = toDateOnly(pedidoCargado.fechaEmision) ?? new Date();
          const vencimComprobante = mapearVencimComprobanteCargado(pedidoCargado, fechaEmisionDate);
          const horaEmisionDate = pedidoCargado.horaEmision
            ? (pedidoCargado.horaEmision instanceof Date ? pedidoCargado.horaEmision : new Date(pedidoCargado.horaEmision))
            : new Date();

          const nroVendedorCargado = Number((pedidoCargado as any).nroVendedor ?? (pedidoCargado as any).NroVendedor ?? 0);

          const pedidoActualizado: PedidoVentaUIExt = {
            ...pedidoCargado,
            fechaEmision: fechaEmisionDate,
            fechaVencim: vencimComprobante.fechaVencim,
            vtoComprob: vencimComprobante.vtoComprob,
            horaEmision: horaEmisionDate,
            ordenCompra: (pedidoCargado as any).ordenCompra || (pedidoCargado as any).ordenDeCompra || '',
            transporte: transporteValue,
            ubicacion: ubicacionFormateada,
            formaDePago: (pedidoCargado as any).formaDePago || '',
            nroSuc: (pedidoCargado as any).nroSuc || DEFAULT_NRO_SUC,
            letra: (pedidoCargado as any).letra || '',
            nroComprobante: Number((pedidoCargado as any).nroComprobante) || 0,
            cuit: (pedidoCargado as any).cuit || '',
            tipoIVA: tipoIvaResuelto || '',
            idCompania: (pedidoCargado as any).idCompania || '',
            nroVendedor: Number.isFinite(nroVendedorCargado) && nroVendedorCargado > 0 ? nroVendedorCargado : 0,
            detalles: detallesConSubtotales,
            importeTotal: pedidoCargado.importeTotal && pedidoCargado.importeTotal > 0
              ? pedidoCargado.importeTotal
              : round2(importeTotalCalculado),
          } as PedidoVentaUIExt;

          setPedidoVenta(pedidoActualizado);
          setLineasPOS(lineasDesdeDetalles(detallesConSubtotales));
          setPosCliente({
            ...clienteDesdeData(pedidoActualizado),
            tipoIva: tipoIvaResuelto || clienteDesdeData(pedidoActualizado).tipoIva,
          });
        } catch (err: unknown) {
          const message = getApiErrorMessage(err, 'Error al cargar el pedido');
          setError(message);
          enqueueSnackbar(message, { variant: 'error', autoHideDuration: 5000 });
          setTimeout(() => navigate('/ventas/pedidos'), 2000);
        } finally {
          setLoadingPedido(false);
        }
      };
      cargarPedido();
    }
  }, [pedidoId, cotizacionId, navigate, enqueueSnackbar]);

  useEffect(() => {
    const cargarCompanias = async () => {
      try {
        const lista = await companiaService.getCompanias({ pageNumber: 1, pageSize: 100 });
        setCompanias(
          lista
            .map((c) => ({
              idCompania: String(c.idCompania ?? '').trim(),
              nombre: String(c.nombre ?? '').trim() || undefined,
              condIva: c.condIva != null ? String(c.condIva).trim() : undefined,
            }))
            .filter((c) => Boolean(c.idCompania)),
        );
      } catch {
        setCompanias([]);
      }
    };
    void cargarCompanias();
  }, []);

  useEffect(() => {
    if (pedidoId || preparandoDesdeCotizacion) {
      sucursalUsuarioEditadaRef.current = true;
      initCompaniaSucursalRef.current = true;
      return;
    }
    sucursalUsuarioEditadaRef.current = false;
    initCompaniaSucursalRef.current = false;
    setPedidoVenta((prev) => ({
      ...prev,
      nroSuc: 0,
      idCompania: '',
    }));
  }, [pedidoId, preparandoDesdeCotizacion, location.key]);

  useEffect(() => {
    if (pedidoId || preparandoDesdeCotizacion || loadingPedido) return;
    if (loadingSucursalesUsuario) return;
    if (sucursales.length === 0 || companias.length === 0) return;
    if (sucursalUsuarioEditadaRef.current) return;
    if (initCompaniaSucursalRef.current) return;

    const inicial = resolveCompaniaYSucursalInicial({
      sucursales,
      preferidaNro,
      preferidaSucursal,
      companias,
    });
    if (!inicial) return;
    initCompaniaSucursalRef.current = true;
    setPedidoVenta((prev) => ({
      ...prev,
      nroSuc: inicial.nroSuc,
      idCompania: inicial.idCompania,
    }));
    persistCompaniaYSucursalInicial(inicial);
  }, [
    sucursales,
    companias,
    pedidoId,
    preparandoDesdeCotizacion,
    loadingPedido,
    loadingSucursalesUsuario,
    preferidaNro,
    preferidaSucursal,
    location.key,
  ]);

  // Solo persistir la preferida (no la elección manual del comprobante)
  useEffect(() => {
    const preferida = resolveSucursalPreferidaNro(preferidaNro, sucursales);
    if (preferida != null) {
      persistGlobalNroSuc(preferida);
    }
  }, [preferidaNro, sucursales]);

  const sucursalesDeCompania = useMemo(
    () => filtrarSucursalesPorCompania(sucursales, pedidoVenta.idCompania),
    [sucursales, pedidoVenta.idCompania],
  );

  const handleIdCompaniaChange = useCallback(
    (id: string) => {
      if (isReadOnly) return;
      const next = String(id ?? '').trim();
      if (next) persistGlobalIdCompania(next);
      const nro = resolveNroSucParaCompania({
        idCompania: next,
        sucursales,
        preferidaNro,
        storedNroSuc: Number(localStorage.getItem('nroSuc') || '0') || null,
        keepCurrentIfValid: false,
      });
      sucursalUsuarioEditadaRef.current = false;
      setPedidoVenta((prev) => ({
        ...prev,
        idCompania: next,
        nroSuc: nro != null && nro > 0 ? nro : 0,
      }));
      if (nro != null && nro > 0) persistGlobalNroSuc(nro);
    },
    [isReadOnly, sucursales, preferidaNro],
  );

  // Debounce: al cambiar nroCliente, enriquecer datos del cliente
  useEffect(() => {
    if (esPedidoPersistente || isReadOnly || preparandoDesdeCotizacion) return undefined;
    const value = pedidoVenta.nroCliente;
    const handle = setTimeout(() => {
      const nro = parseInt((value ?? 0).toString());
      if (!isNaN(nro) && nro > 0) {
        (async () => {
          try {
            const c = await clienteService.getCliente(nro, OPTIONS_CLIENTE_OPERATIVO);
            const vtoComprobCliente = resolveVtoComprobCliente(c);
            const desc = await resolverBonificacionesCliente(c, {
              codigoLista: obtenerCodigoListaCliente(c as any),
              nroVendedor: Number((c as any).nroVendedor ?? (c as any).vendedor) || null,
            });
            setPosCliente(c as unknown as POSCliente);
            setPedidoVenta((prev) => {
              const newCalc = calcularFechaVencimDesdeVtoComprob(
                prev.fechaEmision ?? new Date(),
                vtoComprobCliente,
              );
              const oldCalc = calcularFechaVencimDesdeVtoComprob(
                prev.fechaEmision ?? new Date(),
                prev.vtoComprob ?? null,
              );
              const mantenerVencimManual = !sameCalendarDay(
                prev.fechaVencim ?? prev.fechaEmision,
                oldCalc,
              );
              return {
                ...prev,
                nroCliente: c.nroCliente,
                nombre: c.nombre,
                cuit: c.cuit || '',
                dni: (c as any).dni || '',
                tipoIVA: c.tipoIva || '',
                vtoComprob: vtoComprobCliente,
                fechaVencim: mantenerVencimManual ? (prev.fechaVencim ?? newCalc) : newCalc,
                nroVendedor: Number((c as any).vendedor) > 0 ? Number((c as any).vendedor) : 0,
                nroLista: obtenerCodigoListaCliente(c as any),
                formaDePago: obtenerFormaPagoSugerida(c as any),
                transporte: obtenerTransporteSugerido(c as any),
                porcDesc: desc.porcDesc,
                porcDesc2: desc.porcDesc2,
                porcDesc3: desc.porcDesc3,
              } as PedidoVentaUIExt;
            });
          } catch {}
        })();
      }
    }, 500);
    return () => clearTimeout(handle);
  }, [pedidoVenta.nroCliente, esPedidoPersistente, isReadOnly, preparandoDesdeCotizacion]);

  // Sincronizar lineasPOS → pedidoVenta.detalles (en modos crear y editar)
  useEffect(() => {
    if (modo === 'ver') return;
    const detalles = lineasPOS.map((l, idx): PedidoVentaDetalle => ({
      nroIdformulario: pedidoVenta.nroFormulario,
      nroItem: idx + 1,
      codigoArticulo: l.codigoArticulo,
      descripcionArticulo: l.descripcionArticulo,
      cantidad: l.cantidad,
      precioUnitario: l.precioUnitario,
      descuento: l.descuento,
      porcDesc: l.descuento,
      subtotal: l.subtotal,
      notas: l.notas,
      um: l.um,
      nroFormularioMaster: (l as any).nroFormularioMaster,
      nroItemMaster: (l as any).nroItemMaster,
    }));
    const importeTotal = detalles.reduce((acc, d) => acc + (d.subtotal || 0), 0);
    setPedidoVenta((prev) => ({ ...prev, detalles, importeTotal: round2(importeTotal) }));
  }, [lineasPOS, modo, pedidoVenta.nroFormulario]);

  // ── Handlers cliente ──────────────────────────────────────────
  const handleClienteSelect = (cliente: any) => {
    setPosCliente(cliente as POSCliente);
    const nroClienteSel = cliente.nroCliente ?? cliente.codigo ?? 0;
    const vtoComprobCliente = resolveVtoComprobCliente(cliente);
    setPedidoVenta((prev) => ({
      ...prev,
      nroCliente: nroClienteSel,
      nombre: cliente.nombre,
      cuit: cliente.cuit || '',
      dni: (cliente as any).dni || '',
      tipoIVA: cliente.tipoIva || '',
      vtoComprob: esPedidoPersistente ? prev.vtoComprob : vtoComprobCliente,
      fechaVencim: esPedidoPersistente
        ? (prev.fechaVencim ?? calcularFechaVencimDesdeVtoComprob(prev.fechaEmision ?? new Date(), prev.vtoComprob))
        : calcularFechaVencimDesdeVtoComprob(prev.fechaEmision ?? new Date(), vtoComprobCliente),
      nroVendedor:
        preparandoDesdeCotizacion && nroClienteSel === prev.nroCliente && Number(prev.nroVendedor) > 0
          ? prev.nroVendedor
          : Number((cliente as any).vendedor) > 0
            ? Number((cliente as any).vendedor)
            : 0,
      nroLista:
        preparandoDesdeCotizacion && nroClienteSel === prev.nroCliente && String(prev.nroLista || '').trim() !== ''
          ? String(prev.nroLista || '').trim()
          : obtenerCodigoListaCliente(cliente),
      formaDePago: obtenerFormaPagoSugerida(cliente),
      transporte: obtenerTransporteSugerido(cliente),
    }));
    void resolverBonificacionesCliente(cliente, {
      codigoLista: obtenerCodigoListaCliente(cliente),
      nroVendedor: Number(cliente?.nroVendedor ?? cliente?.vendedor) || null,
    }).then((desc) => {
      setPedidoVenta((prev) => {
        if (Number(prev.nroCliente) !== Number(nroClienteSel)) return prev;
        return { ...prev, porcDesc: desc.porcDesc, porcDesc2: desc.porcDesc2, porcDesc3: desc.porcDesc3 };
      });
    });
  };

  const handleRepetirUltimoPedido = useCallback(async () => {
    const nroCliente = Number(posCliente?.nroCliente ?? pedidoVenta.nroCliente) || 0;
    if (nroCliente <= 0) {
      enqueueSnackbar('Seleccioná un cliente para usar esta opción.', { variant: 'warning' });
      return;
    }
    setLoadingRepetirUltimoPedido(true);
    try {
      const nroLista =
        String(pedidoVenta.nroLista || '').trim()
        || obtenerCodigoListaCliente(posCliente)
        || undefined;
      const result = await construirLineasDesdeUltimoPedido({ nroCliente, nroLista });
      if (!result.ok) {
        enqueueSnackbar(result.message, {
          variant: result.reason === 'sin_historial' ? 'info' : 'warning',
        });
        return;
      }
      setPedidoPersistido(null);
      setLineasPOS(result.lineas);
      setProductosIvaCache((prev) => ({ ...prev, ...result.productosIva }));
      const omitidasMsg =
        result.omitidas > 0
          ? ` (${result.omitidas} línea${result.omitidas > 1 ? 's' : ''} omitida${result.omitidas > 1 ? 's' : ''})`
          : '';
      enqueueSnackbar(
        `Último pedido aplicado: ${result.lineas.length} producto${result.lineas.length > 1 ? 's' : ''}${omitidasMsg}.`,
        { variant: 'success' },
      );
    } catch (e) {
      enqueueSnackbar(getApiErrorMessage(e) || 'No se pudo repetir el último pedido.', {
        variant: 'error',
      });
    } finally {
      setLoadingRepetirUltimoPedido(false);
    }
  }, [posCliente, pedidoVenta.nroCliente, pedidoVenta.nroLista, enqueueSnackbar]);

  const registrarProductoIva = useCallback((codigoArticulo: string, producto: unknown) => {
    const cod = String(codigoArticulo ?? '').trim();
    if (!cod || !producto) return;
    setProductosIvaCache((prev) => {
      if (prev[cod] === producto) return prev;
      return { ...prev, [cod]: producto };
    });
  }, []);

  // ── Handlers líneas POS ───────────────────────────────────────
  const handleAgregarProductoPOS = useCallback((item: SuggestionItem) => {
    setPedidoPersistido(null);
    const cod = String(item.codigoArticulo ?? '').trim();
    if (cod && item.productoIva) {
      registrarProductoIva(cod, item.productoIva);
    }
    const id = genId();
    const umInicial = String(item.um ?? '').trim();
    const precioInicial = Number(item.precioUnitario) || 0;
    const stockInicial = item.stockDisponible ?? null;

    setLineasPOS((prev) => [
      ...prev,
      {
        id,
        codigoArticulo: item.codigoArticulo,
        descripcionArticulo: item.descripcion,
        cantidad: 1,
        precioUnitario: precioInicial,
        descuento: 0,
        subtotal: calcularSubtotal({ cantidad: 1, precioUnitario: precioInicial, descuento: 0 }),
        um: umInicial,
        stockDisponible: stockInicial,
      },
    ]);
    if (!cod) return;

    void (async () => {
      try {
        const producto = await productoService.getProducto(cod, OPTIONS_PRODUCTO_OPERATIVO);
        registrarProductoIva(cod, producto);
        const detalle = await productoService.getEquivalenciasUmDetalle(cod);

        // Precio siempre desde lista/base (UM base) y luego se aplica equivalencia a UM del pedido
        let precioBase = obtenerPrecioBaseProducto(producto);
        const nroLista = String(
          pedidoVenta.nroLista || obtenerCodigoListaCliente(posCliente) || '',
        ).trim();
        if (nroLista) {
          try {
            const reg = await articuloXListaPrecioService.getArticuloXListaPrecio(nroLista, cod);
            const res = resolverPrecioDesdeLista(producto, reg);
            if (res.tipo !== 'sinPrecio') precioBase = res.precioLista;
          } catch {
            /* usar precio de catálogo */
          }
        }

        const r = resolverUmYPrecioDesdeDetalle(producto, precioBase, 'pedido', detalle);
        const umFinal = r.um || umInicial;
        const precioFinal = round2(r.precioUnitario);
        const stockFinal = await fetchStockDisponibleEnUm(cod, umFinal, detalle, producto);
        const porcDesc = await resolverPorcDescLinea(
          contextoDescuentoDesdeCliente(posCliente, {
            codigoLista: nroLista,
            nroVendedor: posCliente?.nroVendedor,
          }),
          producto,
          1,
          umFinal,
        );
        setLineasPOS((prev) =>
          prev.map((l) => {
            if (l.id !== id) return l;
            if (l.descuentoManual) {
              const kept = {
                ...l,
                um: umFinal,
                precioUnitario: precioFinal,
                stockDisponible: stockFinal ?? l.stockDisponible ?? stockInicial,
              };
              kept.subtotal = calcularSubtotal(kept);
              return kept;
            }
            const updated = {
              ...l,
              um: umFinal,
              precioUnitario: precioFinal,
              stockDisponible: stockFinal ?? l.stockDisponible ?? stockInicial,
              descuento: porcDesc,
              descuentoManual: false,
            };
            updated.subtotal = calcularSubtotal(updated);
            return updated;
          }),
        );
      } catch {
        /* conservar precio/UM del buscador */
      }
    })();
  }, [registrarProductoIva, pedidoVenta.nroLista, posCliente]);

  const handleLineaChange = (id: string, field: keyof POSLinea, value: string | number) => {
    setPedidoPersistido(null);
    setLineasPOS((prev) =>
      prev.map((l) => {
        if (l.id !== id) return l;
        const updated = { ...l, [field]: value };
        if (field === 'descuento') updated.descuentoManual = true;
        updated.subtotal = calcularSubtotal(updated);
        return updated;
      }),
    );
  };

  const handleLineaFilled = (id: string, data: Partial<POSLinea>) => {
    setPedidoPersistido(null);
    setLineasPOS((prev) =>
      prev.map((l) => {
        if (l.id !== id) return l;
        const updated = { ...l, ...data };
        updated.subtotal = calcularSubtotal(updated);
        return updated;
      }),
    );
  };

  const handleEliminarLinea = (id: string) => {
    setPedidoPersistido(null);
    setLineasPOS((prev) => prev.filter((l) => l.id !== id));
  };

  const handleDuplicarLinea = (id: string) => {
    setPedidoPersistido(null);
    setLineasPOS((prev) => {
      const idx = prev.findIndex((l) => l.id === id);
      if (idx === -1) return prev;
      const copia: POSLinea = { ...prev[idx], id: genId() };
      const next = [...prev];
      next.splice(idx + 1, 0, copia);
      return next;
    });
  };

  const handleAgregarLineaVacia = () => {
    setPedidoPersistido(null);
    setLineasPOS((prev) => [
      ...prev,
      { id: genId(), codigoArticulo: '', descripcionArticulo: '', cantidad: 1, precioUnitario: 0, descuento: 0, subtotal: 0, um: '' },
    ]);
  };

  const handlePorcDescChange = (field: 'porcDesc' | 'porcDesc2' | 'porcDesc3', v: number) => {
    setPedidoVenta((prev) => ({ ...prev, [field]: Number.isFinite(v) ? v : 0 }));
  };

  const tipoIvaEmision = String(
    posCliente?.tipoIva ?? pedidoVenta.tipoIVA ?? pedidoPersistido?.tipoIva ?? '',
  ).trim().toUpperCase();
  const preciosConIva = esTipoIvaPreciosConIva(tipoIvaEmision);
  const condIvaEmisor = useMemo(() => {
    const id = String(pedidoVenta.idCompania ?? '').trim();
    return companias.find((c) => c.idCompania === id)?.condIva;
  }, [companias, pedidoVenta.idCompania]);

  useEffect(() => {
    const codigos = Array.from(
      new Set(
        lineasPOS
          .map((l) => String(l.codigoArticulo ?? '').trim())
          .filter((c) => c.length > 0),
      ),
    );
    if (codigos.length === 0) return;

    let cancelled = false;
    (async () => {
      let missing: string[] = [];
      setProductosIvaCache((prev) => {
        missing = codigos.filter((c) => !prev[c]);
        return prev;
      });
      if (missing.length === 0) return;

      const fetched: Record<string, unknown> = {};
      await Promise.all(
        missing.map(async (codigo) => {
          try {
            const producto = await productoService.getProducto(codigo, OPTIONS_PRODUCTO_OPERATIVO);
            if (producto) fetched[codigo] = producto;
          } catch {
            /* sin producto */
          }
        }),
      );
      if (!cancelled && Object.keys(fetched).length > 0) {
        setProductosIvaCache((prev) => ({ ...prev, ...fetched }));
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [lineasPOS]);

  // ── Totales ───────────────────────────────────────────────────
  const subtotalNeto = lineasPOS.reduce((acc, l) => acc + l.subtotal, 0);
  const importeDesc1 = (subtotalNeto * (pedidoVenta.porcDesc || 0)) / 100;
  const baseDesc2 = subtotalNeto - importeDesc1;
  const importeDesc2 = (baseDesc2 * (pedidoVenta.porcDesc2 || 0)) / 100;
  const baseDesc3 = baseDesc2 - importeDesc2;
  const importeDesc3 = (baseDesc3 * (pedidoVenta.porcDesc3 || 0)) / 100;
  const subtotalConDesc = baseDesc3 - importeDesc3;

  const totalesIva = useMemo(() => {
    const recalc = calcularTotalesIvaVenta({
      lineas: lineasPOS.map((l) => ({
        codigoArticulo: l.codigoArticulo,
        subtotal: l.subtotal,
      })),
      subtotalNeto,
      subtotalConDesc,
      tipoIva: tipoIvaEmision,
      productosCache: productosIvaCache,
      condIvaEmisor,
    });

    if (pedidoPersistido && (pedidoPersistido.importeTotal ?? 0) > 0) {
      const importeTotal = pedidoPersistido.importeTotal ?? 0;
      const importeSinIva = pedidoPersistido.importeSinIva ?? recalc.subtotalConDesc;
      const montoIVA = pedidoPersistido.ivainscripto ?? recalc.montoIVA;
      const impuestosArray =
        recalc.impuestosArray.length > 0
          ? recalc.impuestosArray
          : montoIVA > 0 && importeSinIva > 0
            ? [{ tasa: round2((montoIVA / importeSinIva) * 100), monto: montoIVA }]
            : [];

      return {
        subtotalConDesc: importeSinIva,
        impuestosArray,
        montoIVA,
        total: importeTotal,
      };
    }

    return recalc;
  }, [
    lineasPOS,
    subtotalNeto,
    subtotalConDesc,
    tipoIvaEmision,
    productosIvaCache,
    condIvaEmisor,
    pedidoPersistido,
  ]);

  const { impuestosArray: impuestos, montoIVA: iva, total } = totalesIva;

  const totalesDisplay = useMemo(() => {
    if (pedidoPersistido) {
      const sumSubtotalFinal = round2(
        lineasPOS.reduce((acc, l) => acc + (l.subtotalFinal ?? 0), 0),
      );
      const subtotalDisplay = preciosConIva
        ? sumSubtotalFinal > 0
          ? sumSubtotalFinal
          : round2((pedidoPersistido.importeSinIva ?? 0) + (pedidoPersistido.ivainscripto ?? 0))
        : subtotalNeto;
      const d1 = pedidoVenta.porcDesc || 0;
      const d2 = pedidoVenta.porcDesc2 || 0;
      const d3 = pedidoVenta.porcDesc3 || 0;
      const importeDesc1Display = round2(subtotalDisplay - aplicarPorcentajeDescuento(subtotalDisplay, d1));
      const baseDesc2Display = aplicarPorcentajeDescuento(subtotalDisplay, d1);
      const importeDesc2Display = round2(baseDesc2Display - aplicarPorcentajeDescuento(baseDesc2Display, d2));
      const baseDesc3Display = aplicarPorcentajeDescuento(baseDesc2Display, d2);
      const importeDesc3Display = round2(baseDesc3Display - aplicarPorcentajeDescuento(baseDesc3Display, d3));
      const totalDisplay = aplicarPorcentajeDescuento(baseDesc3Display, d3);

      return {
        subtotalDisplay,
        importeDesc1Display,
        importeDesc2Display,
        importeDesc3Display,
        totalDisplay,
      };
    }

    const subtotalDisplay = preciosConIva
      ? round2(
          lineasPOS.reduce((acc, l) => {
            const codigo = String(l.codigoArticulo ?? '').trim();
            const mult = multiplicadorIvaProducto(productosIvaCache[codigo], tipoIvaEmision, condIvaEmisor);
            return acc + subtotalLineaConIvaDisplay(l.precioUnitario, l.cantidad, l.descuento, mult);
          }, 0),
        )
      : subtotalNeto;
    const d1 = pedidoVenta.porcDesc || 0;
    const d2 = pedidoVenta.porcDesc2 || 0;
    const d3 = pedidoVenta.porcDesc3 || 0;
    const importeDesc1Display = round2(subtotalDisplay - aplicarPorcentajeDescuento(subtotalDisplay, d1));
    const baseDesc2Display = aplicarPorcentajeDescuento(subtotalDisplay, d1);
    const importeDesc2Display = round2(baseDesc2Display - aplicarPorcentajeDescuento(baseDesc2Display, d2));
    const baseDesc3Display = aplicarPorcentajeDescuento(baseDesc2Display, d2);
    const importeDesc3Display = round2(baseDesc3Display - aplicarPorcentajeDescuento(baseDesc3Display, d3));
    const totalDisplay = aplicarPorcentajeDescuento(baseDesc3Display, d3);

    return {
      subtotalDisplay,
      importeDesc1Display,
      importeDesc2Display,
      importeDesc3Display,
      totalDisplay,
    };
  }, [
    pedidoPersistido,
    preciosConIva,
    lineasPOS,
    productosIvaCache,
    tipoIvaEmision,
    condIvaEmisor,
    subtotalNeto,
    pedidoVenta.porcDesc,
    pedidoVenta.porcDesc2,
    pedidoVenta.porcDesc3,
  ]);

  const totalMostrado = truncToDecimals(
    preciosConIva ? (totalesDisplay.totalDisplay ?? total) : total,
    2,
  );

  // ── Guardar ──────────────────────────────────────────────────
  const handleSave = async () => {
    if (isReadOnly) {
      enqueueSnackbar('No se puede guardar un pedido en modo solo lectura', { variant: 'warning' });
      return;
    }

    try {
      setLoading(true);
      setError(null);

      const erroresValidacion: string[] = [];
      if (!pedidoVenta.nroCliente || pedidoVenta.nroCliente <= 0)
        erroresValidacion.push('Debe seleccionar un cliente');
      if (!pedidoVenta.fechaEmision)
        erroresValidacion.push('Debe ingresar la Fecha de Emisión');
      const fechaVencimError = validarFechasFacturaVenta(
        pedidoVenta.fechaEmision,
        pedidoVenta.fechaVencim ?? pedidoVenta.fechaEmision,
        calcularFechaVencimDesdeVtoComprob(
          pedidoVenta.fechaEmision,
          pedidoVenta.vtoComprob,
        ),
      );
      if (fechaVencimError) erroresValidacion.push(fechaVencimError);
      if (!(pedidoVenta as PedidoVentaUI).ubicacion?.trim())
        erroresValidacion.push('Debe seleccionar una Ubicación');
      if (!(pedidoVenta as PedidoVentaUI).formaDePago?.trim())
        erroresValidacion.push('Debe seleccionar una Forma de Pago');
      if (!pedidoVenta.nroSuc || pedidoVenta.nroSuc <= 0)
        erroresValidacion.push('Debe seleccionar una Sucursal / Punto de Venta');
      if (!pedidoVenta.detalles || pedidoVenta.detalles.length === 0) {
        erroresValidacion.push('Debe agregar al menos un producto al pedido');
      } else {
        pedidoVenta.detalles.forEach((detalle, index) => {
          if (!detalle.cantidad || detalle.cantidad <= 0)
            erroresValidacion.push(`El producto "${detalle.descripcionArticulo || detalle.codigoArticulo}" (item ${index + 1}) debe tener una cantidad mayor a 0`);
          if (!detalle.codigoArticulo)
            erroresValidacion.push(`El item ${index + 1} no tiene código de artículo`);
        });
      }

      if (erroresValidacion.length > 0) {
        erroresValidacion.forEach((e) => enqueueSnackbar(e, { variant: 'error', autoHideDuration: 5000 }));
        setError(erroresValidacion.join('\n'));
        setLoading(false);
        return;
      }

      advertirLineasConPrecioCeroPedido(pedidoVenta.detalles, enqueueSnackbar);

      const payload = {
        ...pedidoVenta,
        nroSuc: pedidoVenta.nroSuc,
        ordenCompra: pedidoVenta.ordenCompra || '',
        transporte: pedidoVenta.transporte || '',
        ubicacion: pedidoVenta.ubicacion || '',
        formaDePago: pedidoVenta.formaDePago || '',
        nroFormularioCotizacion: (!isEditMode && cotizacionId && !isNaN(cotizacionId)) ? cotizacionId : undefined,
      } as any;

      let nroPedido: number | null = null;

      if (isEditMode && pedidoId) {
        const id = parseInt(pedidoId, 10);
        await pedidoVentaService.updatePedidoVenta(id, payload);
        nroPedido = id;
        enqueueSnackbar('Pedido actualizado exitosamente', { variant: 'success', autoHideDuration: 3000 });
        setTimeout(() => navigate('/ventas/pedidos'), 1500);
        setLoading(false);
        return;
      }

      const response = await pedidoVentaService.createPedidoVenta(payload);
      if (response?.nroFormulario) nroPedido = response.nroFormulario;
      else if ((response as any)?.formulario?.nroFormulario) nroPedido = (response as any).formulario.nroFormulario;
      else if ((response as any)?.data?.nroFormulario) nroPedido = (response as any).data.nroFormulario;

      if (nroPedido) {
        setNroPedidoCreado(nroPedido);
        setSuccessDialogOpen(true);
        setPedidoVenta(pedidoVentaInicial);
        setPosCliente(null);
        setLineasPOS([]);
      } else {
        enqueueSnackbar('Pedido de venta guardado exitosamente', { variant: 'success' });
        setPedidoVenta(pedidoVentaInicial);
        setPosCliente(null);
        setLineasPOS([]);
      }
    } catch (err: any) {
      const data = err?.response?.data;
      const status = err?.response?.status;
      let errorMessage = 'Error al guardar el pedido de venta';

      if (err?.message && (err.message.includes('Se encontraron') || err.message.includes('obligatorio'))) {
        const partes = err.message.split('\n');
        const errores = partes.slice(1).map((e: string) => e.replace(/^\d+\.\s*/, '').trim()).filter(Boolean);
        errores.forEach((e: string) => enqueueSnackbar(e, { variant: 'error', autoHideDuration: 6000 }));
        errorMessage = err.message;
      } else if (data?.errors && typeof data.errors === 'object') {
        const msgs: string[] = [];
        for (const k of Object.keys(data.errors)) {
          const val = data.errors[k];
          msgs.push(Array.isArray(val) ? `${k}: ${val.join(' - ')}` : `${k}: ${String(val)}`);
        }
        if (msgs.length > 0) {
          msgs.forEach((m: string) => enqueueSnackbar(m, { variant: 'error', autoHideDuration: 6000 }));
          errorMessage = msgs.join('\n');
        }
      } else if (status === 400 || status === 500) {
        errorMessage = getApiErrorMessage(err, errorMessage);
        enqueueSnackbar(errorMessage, { variant: 'error', autoHideDuration: 8000 });
      } else {
        const msg = getApiErrorMessage(err, '');
        if (msg) {
          errorMessage = msg.length > 200 ? msg.substring(0, 200) + '...' : msg;
          enqueueSnackbar(errorMessage, { variant: 'error', autoHideDuration: 8000 });
        }
      }
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleSuccessContinuar = () => {
    setSuccessDialogOpen(false);
    setNroPedidoCreado(null);
    navigate('/ventas/pedidos');
  };

  const nroLista = obtenerCodigoListaCliente(posCliente) || undefined;

  return (
    <Box sx={{ p: { xs: 2, sm: 3 }, bgcolor: '#f5f5f5', minHeight: '100vh', maxWidth: '100%', overflowX: 'hidden' }}>
      <Card sx={{ bgcolor: '#ffffff', borderRadius: 3, boxShadow: '0 2px 8px rgba(0,0,0,0.1)', overflow: 'hidden' }}>
        <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            alignItems={{ xs: 'flex-start', sm: 'center' }}
            spacing={1.5}
            sx={{ mb: 3, flexWrap: 'wrap', gap: 1 }}
          >
            <Button
              startIcon={<ArrowBackIcon />}
              onClick={() => navigate('/ventas/pedidos')}
              sx={{ textTransform: 'none' }}
            >
              Pedidos
            </Button>
            <Typography variant="h4" sx={{ fontWeight: 600, color: '#1976d2' }}>
              {(() => {
                const nroDoc = formatNroDocumentoVenta(
                  pedidoVenta.letra,
                  pedidoVenta.nroSuc,
                  pedidoVenta.nroComprobante,
                );
                if (isReadOnly) return nroDoc ? `Ver Pedido ${nroDoc}` : 'Ver Pedido';
                if (isEditMode) return nroDoc ? `Editar Pedido ${nroDoc}` : 'Editar Pedido';
                return 'Nuevo Pedido de Venta';
              })()}
            </Typography>
            {!isReadOnly && cotizacionId && infoCotizacion && (
              <Chip
                label={
                  infoCotizacion.letra && (infoCotizacion.puntoVenta ?? 0) > 0 && (infoCotizacion.nroComprobante ?? 0) > 0
                    ? `Desde cotización ${infoCotizacion.letra}-${String(infoCotizacion.puntoVenta ?? 0).padStart(5, '0')}-${String(infoCotizacion.nroComprobante ?? 0).padStart(8, '0')}`
                    : 'Desde cotización'
                }
                color="info"
                size="small"
              />
            )}
          </Stack>

          {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

          {loadingPedido ? (
            <Box sx={{ p: 2, textAlign: 'center' }}><Typography>Cargando pedido...</Typography></Box>
          ) : (
            <Grid container spacing={2} alignItems="flex-start">
          {/* Columna principal */}
          <Grid item xs={12} md={8}>
            <Stack spacing={2.5}>
              <Box>
                <Typography variant="overline" color="text.secondary" display="block" mb={0.75}>Cliente</Typography>
                <FacturaPOSClientePanel
                  cliente={posCliente}
                  onClienteSelect={handleClienteSelect}
                  readOnly={isReadOnly}
                  formaDePago={pedidoVenta.formaDePago || ''}
                  ordenCompra={pedidoVenta.ordenCompra || ''}
                  onRepetirUltimoPedido={
                    puedeRepetirUltimoPedido ? handleRepetirUltimoPedido : undefined
                  }
                  loadingRepetirUltimoPedido={loadingRepetirUltimoPedido}
                  onVerInfo360={
                    posCliente?.nroCliente
                      ? () => setInfo360Cliente(posCliente.nroCliente)
                      : undefined
                  }
                />
              </Box>
              {!isReadOnly && (
                <Box>
                  <Typography variant="overline" color="text.secondary" display="block" mb={0.75}>Agregar artículo</Typography>
                  <FacturaPOSSearchBar
                    nroLista={nroLista}
                    contextoUm="pedido"
                    tipoIvaEmision={tipoIvaEmision}
                    condIvaEmisor={condIvaEmisor}
                    preciosConIva={preciosConIva}
                    disabled={!posCliente}
                    onAgregarProducto={handleAgregarProductoPOS}
                  />
                </Box>
              )}
              <Box>
                <Stack direction="row" alignItems="center" justifyContent="space-between" mb={0.75}>
                  <Typography variant="overline" color="text.secondary">Artículos</Typography>
                  {lineasPOS.length > 0 && (
                    <Chip label={`${lineasPOS.length} renglonn${lineasPOS.length > 1 ? 'es' : ''}`} size="small" />
                  )}
                </Stack>
                <FacturaPOSLineasGrid
                  lineas={lineasPOS}
                  nroLista={nroLista}
                  contextoDescuento={contextoDescuentoDesdeCliente(posCliente, {
                    codigoLista: nroLista,
                    nroVendedor: posCliente?.nroVendedor ?? (Number(pedidoVenta.nroVendedor) || null),
                  })}
                  contextoUm="pedido"
                  tipoIvaEmision={tipoIvaEmision}
                  condIvaEmisor={condIvaEmisor}
                  productosIvaCache={productosIvaCache}
                  preciosConIva={preciosConIva}
                  usarPreciosPersistidosEnLineas={Boolean(pedidoPersistido)}
                  onLineaChange={handleLineaChange}
                  onLineaFilled={handleLineaFilled}
                  onProductoIvaLoaded={registrarProductoIva}
                  onEliminarLinea={handleEliminarLinea}
                  onDuplicarLinea={handleDuplicarLinea}
                  onAgregarLineaVacia={handleAgregarLineaVacia}
                  readOnly={isReadOnly}
                  emptyHint="Buscá un artículo arriba para agregarlo al pedido"
                />
              </Box>
            </Stack>
          </Grid>

          {/* Panel lateral */}
          <Grid item xs={12} md={4}>
            <Box sx={{ position: { md: 'sticky' }, top: { md: 16 } }}>
              <FacturaPOSTotalesPanel
                subtotalNeto={subtotalNeto}
                preciosConIva={preciosConIva}
                subtotalDisplay={totalesDisplay.subtotalDisplay}
                importeDesc1Display={totalesDisplay.importeDesc1Display}
                importeDesc2Display={totalesDisplay.importeDesc2Display}
                importeDesc3Display={totalesDisplay.importeDesc3Display}
                porcDesc={pedidoVenta.porcDesc || 0}
                porcDesc2={pedidoVenta.porcDesc2 || 0}
                porcDesc3={pedidoVenta.porcDesc3 || 0}
                onPorcDescChange={handlePorcDescChange}
                impuestos={impuestos}
                montoIVA={iva}
                total={totalMostrado}
                observaciones={pedidoVenta.descripcion ?? ''}
                onObservacionesChange={(v) =>
                  setPedidoVenta((prev) => ({ ...prev, descripcion: v }))
                }
                cliente={posCliente}
                fechaEmision={toLocalDateStringYMD(
                  pedidoVenta.fechaEmision instanceof Date
                    ? pedidoVenta.fechaEmision
                    : new Date(pedidoVenta.fechaEmision as string | number),
                )}
                onFechaEmisionChange={(ymd) =>
                  setPedidoVenta((prev) => {
                    const fechaEmision = parseLocalDate(ymd);
                    const nuevoSugerido = calcularFechaVencimDesdeVtoComprob(
                      fechaEmision,
                      prev.vtoComprob,
                    );
                    const sugeridoAnterior = calcularFechaVencimDesdeVtoComprob(
                      prev.fechaEmision ?? new Date(),
                      prev.vtoComprob,
                    );
                    const mantenerManual = !sameCalendarDay(
                      prev.fechaVencim ?? prev.fechaEmision,
                      sugeridoAnterior,
                    );
                    return {
                      ...prev,
                      fechaEmision,
                      fechaVencim: mantenerManual ? (prev.fechaVencim ?? nuevoSugerido) : nuevoSugerido,
                    };
                  })
                }
                fechaVencim={toLocalDateInputValue(pedidoVenta.fechaVencim ?? pedidoVenta.fechaEmision)}
                fechaVencimMin={toLocalDateInputValue(
                  calcularFechaVencimDesdeVtoComprob(
                    pedidoVenta.fechaEmision,
                    pedidoVenta.vtoComprob,
                  ),
                )}
                fechaVencimHelperText={
                  pedidoVenta.vtoComprob != null
                    ? 'Puede editarse, pero no ser anterior a la fecha sugerida'
                    : 'Si no hay plazo comercial, vence en la fecha de emisión'
                }
                onFechaVencimChange={(ymd) =>
                  setPedidoVenta((prev) => ({ ...prev, fechaVencim: parseLocalDate(ymd) }))
                }
                idCompania={pedidoVenta.idCompania ?? ''}
                onIdCompaniaChange={handleIdCompaniaChange}
                companias={companias}
                sucursales={sucursalesDeCompania}
                nroSuc={Number(pedidoVenta.nroSuc) || 0}
                onNroSucChange={(n) => {
                  sucursalUsuarioEditadaRef.current = true;
                  const suc = sucursales.find((s) => s.nroSuc === n);
                  const idFromSuc = String(suc?.idCompania ?? '').trim();
                  setPedidoVenta((prev) => ({
                    ...prev,
                    nroSuc: n,
                    ...(idFromSuc ? { idCompania: idFromSuc } : {}),
                  }));
                  if (idFromSuc) persistGlobalIdCompania(idFromSuc);
                }}
                loadingSucursales={loadingSucursales}
                nroVendedor={Number(pedidoVenta.nroVendedor) || posCliente?.nroVendedor || 0}
                nombreVendedorFallback={posCliente?.vendedorInfo?.nombreVendedor ?? undefined}
                onNroVendedorChange={(nro, nombre) => {
                  setPedidoVenta((prev) => ({ ...prev, nroVendedor: nro ?? 0 }));
                  setPosCliente((prev) => {
                    if (!prev) return prev;
                    if (!nro) return { ...prev, nroVendedor: undefined, vendedorInfo: undefined };
                    return {
                      ...prev,
                      nroVendedor: nro,
                      vendedorInfo: { nroVendedor: nro, nombreVendedor: nombre },
                    };
                  });
                }}
                codigoLista={
                  obtenerCodigoListaCliente(posCliente) || String(pedidoVenta.nroLista ?? '').trim()
                }
                onCodigoListaChange={(codigo, descripcion) => {
                  const c = String(codigo ?? '').trim();
                  setPosCliente((prev) => {
                    if (!prev) return prev;
                    if (!c) return { ...prev, nroLista: undefined, listaPrecio: undefined };
                    return {
                      ...prev,
                      nroLista: c,
                      listaPrecio: {
                        codigoLista: c,
                        descripcion: descripcion ?? prev.listaPrecio?.descripcion ?? null,
                      },
                    };
                  });
                  setPedidoVenta((prev) => ({ ...prev, nroLista: c }));
                }}
                formaDePago={pedidoVenta.formaDePago || ''}
                onFormaDePagoChange={(v) => setPedidoVenta((prev) => ({ ...prev, formaDePago: v }))}
                ordenCompra={pedidoVenta.ordenCompra || ''}
                onOrdenCompraChange={(v) => setPedidoVenta((prev) => ({ ...prev, ordenCompra: v }))}
                transporte={pedidoVenta.transporte || ''}
                onTransporteChange={(v) => setPedidoVenta((prev) => ({ ...prev, transporte: v }))}
                ubicacion={pedidoVenta.ubicacion || ''}
                onUbicacionChange={(v) => setPedidoVenta((prev) => ({ ...prev, ubicacion: v }))}
                saving={loading}
                onGuardar={() => handleSave()}
                cantLineas={lineasPOS.length}
                readOnly={isReadOnly}
                hideDraftButton
                saveLabel={isEditMode ? 'Actualizar Pedido' : 'Guardar Pedido'}
                emisionNombre={posCliente?.nombre ?? pedidoVenta.nombre ?? ''}
                onEmisionNombreChange={(v) => {
                  setPosCliente((p) => (p ? { ...p, nombre: v, razonSocial: v } : p));
                  setPedidoVenta((prev) => ({ ...prev, nombre: v }));
                }}
                emisionCuit={posCliente?.cuit ?? pedidoVenta.cuit ?? ''}
                onEmisionCuitChange={(v) => {
                  setPosCliente((p) => (p ? { ...p, cuit: v } : p));
                  setPedidoVenta((prev) => ({ ...prev, cuit: v }));
                }}
                emisionTipoIva={posCliente?.tipoIva ?? pedidoVenta.tipoIVA ?? ''}
                onEmisionTipoIvaChange={(v) => {
                  setPosCliente((p) => (p ? { ...p, tipoIva: v } : p));
                  setPedidoVenta((prev) => ({ ...prev, tipoIVA: v }));
                }}
                nroInterno={pedidoVenta.nroFormulario || Number(pedidoId) || 0}
              />
            </Box>
          </Grid>
            </Grid>
          )}
        </CardContent>
      </Card>

      <Dialog
        open={successDialogOpen}
        onClose={(_, reason) => {
          if (reason === 'backdropClick') return;
          handleSuccessContinuar();
        }}
        maxWidth="xs"
        fullWidth
        PaperProps={{ sx: { borderRadius: 2 } }}
      >
        <DialogTitle sx={{ fontWeight: 700, pb: 1.5, pt: 3, px: 3 }}>Pedido creado</DialogTitle>
        <DialogContent sx={{ pt: 1, pb: 2, px: 3 }}>
          <Typography variant="body1" color="text.secondary">
            El pedido se creó correctamente. Número de pedido: {nroPedidoCreado}
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 3, pt: 1 }}>
          <Button
            onClick={handleSuccessContinuar}
            variant="contained"
            color="primary"
            sx={{ textTransform: 'none' }}
          >
            Continuar
          </Button>
        </DialogActions>
      </Dialog>

      <ClienteInfo360Dialog
        open={info360Cliente != null}
        nroCliente={info360Cliente}
        onClose={() => setInfo360Cliente(null)}
      />
    </Box>
  );
};

export default PedidosVenta;
